import Head from 'next/head'
import { useRouter } from 'next/router'

import type { FC, ReactNode } from 'react'
import type { SeoProps } from '@/types'

import { siteConfig } from '@/constants/site'
import { defaultLocale, locales } from '@/i18n'
import { useSharedTranslation } from '@/i18n/shared'

export const Seo: FC<SeoProps> = ({
    title,
    description,
    ogImage = siteConfig.ogImage
}): ReactNode => {
    const router = useRouter()
    const s = useSharedTranslation()

    // Fall back to the site-wide description in the visitor's own language.
    const metaDescription = description ?? s.seo.siteDescription
    const pageTitle = title ? `${title} | ${siteConfig.name}` : siteConfig.name
    const image = ogImage?.startsWith('http') ? ogImage : `${siteConfig.url}${ogImage ?? ''}`

    const path = router.asPath.split('?')[0].split('#')[0]
    const cleanPath = path === '/' ? '' : path
    const locale = router.locale ?? defaultLocale

    const localizedUrl = (loc: string) =>
        loc === defaultLocale ? `${siteConfig.url}${cleanPath || '/'}` : `${siteConfig.url}/${loc}${cleanPath}`

    const canonical = localizedUrl(locale)

    return (
        <Head>
            <title>{pageTitle}</title>

            <link rel='canonical' href={canonical} />
            {locales.map((loc) => (
                <link key={loc} rel='alternate' hrefLang={loc} href={localizedUrl(loc)} />
            ))}
            <link rel='alternate' hrefLang='x-default' href={localizedUrl(defaultLocale)} />

            <meta name='description' content={metaDescription} />
            <meta property='og:title' content={pageTitle} />
            <meta property='og:description' content={metaDescription} />
            <meta property='og:type' content='website' />
            <meta property='og:url' content={canonical} />
            <meta property='og:image' content={image} />
            <meta name='twitter:card' content='summary_large_image' />
            <meta name='twitter:title' content={pageTitle} />
            <meta name='twitter:description' content={metaDescription} />
            <meta name='twitter:image' content={image} />
        </Head>
    )
}
